<script setup>
import { ref, computed, watch } from 'vue'
import SubjectSelectionModal from '@/Components/Modals/SubjectSelectionModal.vue'

const props = defineProps({
  pricingLimits: {
    type: Object,
    required: true
  },
  userSubjects: {
    type: Array,
    default: () => []
  },
  userLevels: {
    type: Array,
    default: () => []
  },
  initialSelectedSubjects: {
    type: Array,
    default: () => []
  },
  initialPricing: {
    type: Object,
    default: () => ({})
  },
  hasBasicMethods: {
    type: Boolean,
    default: false
  },
  hasSilverMethods: {
    type: Boolean,
    default: false
  },
  hasGoldMethods: {
    type: Boolean,
    default: false
  },
  errors: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['update:pricing', 'update:subjects'])

// Pricing state - prioritize initial pricing, fall back to minimum
const basicPrice = ref(
  (props.initialPricing?.basic && props.initialPricing.basic > 0)
    ? props.initialPricing.basic
    : (props.pricingLimits?.base?.min || 10)
)
const silverPrice = ref(
  (props.initialPricing?.silver && props.initialPricing.silver > 0)
    ? props.initialPricing.silver
    : (props.pricingLimits?.silver?.min || 50)
)
const goldPrice = ref(
  (props.initialPricing?.gold && props.initialPricing.gold > 0)
    ? props.initialPricing.gold
    : (props.pricingLimits?.gold?.min || 150)
)

// Selected subjects for group tutoring
const selectedGroupSubjects = ref([...props.initialSelectedSubjects])
const selectedGroupSubjectsCount = computed(() => selectedGroupSubjects.value.length)

// Watch for changes to initialSelectedSubjects prop
watch(() => props.initialSelectedSubjects, (newSubjects) => {
  selectedGroupSubjects.value = [...newSubjects]
}, { deep: true })

// Watch for changes to initialPricing prop
watch(() => props.initialPricing, (newPricing) => {
  if (newPricing?.basic && newPricing.basic > 0) {
    basicPrice.value = newPricing.basic
  } else if (!newPricing?.basic || newPricing.basic === 0) {
    basicPrice.value = props.pricingLimits?.base?.min || 10
  }

  if (newPricing?.silver && newPricing.silver > 0) {
    silverPrice.value = newPricing.silver
  } else if (!newPricing?.silver || newPricing.silver === 0) {
    silverPrice.value = props.pricingLimits?.silver?.min || 50
  }

  if (newPricing?.gold && newPricing.gold > 0) {
    goldPrice.value = newPricing.gold
  } else if (!newPricing?.gold || newPricing.gold === 0) {
    goldPrice.value = props.pricingLimits?.gold?.min || 150
  }
}, { deep: true })

// Modal state
const showSubjectModal = ref(false)

const openSubjectModal = () => {
  showSubjectModal.value = true
}

const handleSubjectsUpdate = (subjects) => {
  selectedGroupSubjects.value = subjects
  emit('update:subjects', subjects)
}

// Calculate slider color based on price position
const getSliderColor = (price, min, max, recommended) => {
  if (price > recommended) {
    return 'accent-amber-500' // Above recommended
  }
  return 'accent-blue-500' // At or below recommended
}

const basicSliderColor = computed(() => {
  const recommended = props.pricingLimits?.base?.average || 10
  return getSliderColor(basicPrice.value, props.pricingLimits?.base?.min || 5, props.pricingLimits?.base?.max || 25, recommended)
})

const silverSliderColor = computed(() => {
  const recommended = props.pricingLimits?.silver?.average || 50
  return getSliderColor(silverPrice.value, props.pricingLimits?.silver?.min || 20, props.pricingLimits?.silver?.max || 80, recommended)
})

const goldSliderColor = computed(() => {
  const recommended = props.pricingLimits?.gold?.average || 150
  return getSliderColor(goldPrice.value, props.pricingLimits?.gold?.min || 100, props.pricingLimits?.gold?.max || 200, recommended)
})

// Helper to get gradient color hex value
const getGradientColor = (price, average) => {
  return price > average ? '#f59e0b' : '#3b82f6'
}

// Computed gradient colors for each tier
const basicGradientColor = computed(() =>
  getGradientColor(basicPrice.value, props.pricingLimits?.base?.average || 10)
)

const silverGradientColor = computed(() =>
  getGradientColor(silverPrice.value, props.pricingLimits?.silver?.average || 50)
)

const goldGradientColor = computed(() =>
  getGradientColor(goldPrice.value, props.pricingLimits?.gold?.average || 150)
)

// Emit pricing updates
watch([basicPrice, silverPrice, goldPrice], () => {
  emit('update:pricing', {
    basic: basicPrice.value,
    silver: silverPrice.value,
    gold: goldPrice.value
  })
})
</script>

<template>
  <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
    <!-- Basic Plan -->
    <section id="base-plan-pricing">
      <div
        class="border-2 rounded-2xl p-8 h-full transition-all flex flex-col"
        :class="hasBasicMethods
          ? 'bg-white border-gray-200 hover:border-blue-300 hover:shadow-lg'
          : 'bg-gray-50 border-gray-300 opacity-60'"
      >
        <div class="flex items-center space-x-4 mb-6">
          <div class="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center">
            <span class="material-symbols-outlined text-blue-600 text-3xl">play_circle</span>
          </div>
          <div>
            <h3 class="text-xl font-bold text-gray-900">Content Library</h3>
            <p class="text-sm text-gray-500">Video content & study materials</p>
          </div>
        </div>
        <div class="space-y-3 mb-8">
          <div class="flex items-center space-x-3">
            <span class="material-symbols-outlined text-green-500">check</span>
            <span class="text-sm text-gray-700">Recorded study content</span>
          </div>
          <div class="flex items-center space-x-3">
            <span class="material-symbols-outlined text-green-500">check</span>
            <span class="text-sm text-gray-700">Downloadable study materials</span>
          </div>
          <div class="flex items-center space-x-3">
            <span class="material-symbols-outlined text-green-500">check</span>
            <span class="text-sm text-gray-700">Practice worksheets</span>
          </div>
          <div class="flex items-center space-x-3">
            <span class="material-symbols-outlined text-green-500">check</span>
            <span class="text-sm text-gray-700">Access to resource library</span>
          </div>
        </div>

        <!-- Spacer to push pricing to bottom -->
        <div class="flex-grow"></div>

        <!-- Not Offered Message -->
        <div v-if="!hasBasicMethods" class="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
          <div class="flex items-center gap-2 text-yellow-800">
            <span class="material-symbols-outlined text-lg">info</span>
            <p class="text-sm font-medium">You do not offer this service</p>
          </div>
          <p class="text-xs text-yellow-700 mt-1">Select video/study material methods in Services Offered to enable this tier</p>
        </div>

        <!-- Pricing Control -->
        <div v-else class="pricing-control mt-6">
          <div class="flex justify-between items-center mb-4">
            <span class="text-sm font-semibold text-gray-700">Weekly Price</span>
            <span class="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
              Recommended: £{{ pricingLimits.base?.average || 10 }}
            </span>
          </div>
          <div class="relative mb-6">
            <input
              v-model.number="basicPrice"
              type="range"
              :min="pricingLimits.base?.min || 5"
              :max="pricingLimits.base?.max || 25"
              class="w-full h-3 rounded-lg appearance-none cursor-pointer slider"
              :class="basicSliderColor"
              :style="{
                background: `linear-gradient(to right,
                  ${basicGradientColor} 0%,
                  ${basicGradientColor} ${((basicPrice - (pricingLimits.base?.min || 5)) / ((pricingLimits.base?.max || 25) - (pricingLimits.base?.min || 5))) * 100}%,
                  #e5e7eb ${((basicPrice - (pricingLimits.base?.min || 5)) / ((pricingLimits.base?.max || 25) - (pricingLimits.base?.min || 5))) * 100}%,
                  #e5e7eb 100%)`
              }"
            >
            <div class="flex justify-between text-xs text-gray-500 mt-2">
              <span>£{{ pricingLimits.base?.min || 5 }}</span>
              <span>£{{ pricingLimits.base?.max || 25 }}</span>
            </div>
          </div>
          <div class="text-center bg-gray-50 rounded-xl py-6">
            <span class="text-4xl font-bold text-gray-900">£{{ basicPrice }}</span>
            <span class="text-gray-500 text-lg font-bold"> weekly subscription</span>
          </div>
          <!-- Error message for basic pricing -->
          <div class="mt-3 min-h-[3rem]">
            <div v-if="errors['pricing.base']" class="p-3 bg-red-50 border border-red-200 rounded-lg">
              <div class="flex items-start gap-2">
                <span class="material-symbols-outlined text-red-600 text-sm">error</span>
                <p class="text-sm text-red-700">{{ errors['pricing.base'] }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Silver Plan -->
    <section id="silver-plan-pricing">
      <div
        class="border-2 rounded-2xl p-8 h-full transition-all flex flex-col"
        :class="hasSilverMethods
          ? 'bg-white border-purple-200 hover:border-purple-400 hover:shadow-lg'
          : 'bg-gray-50 border-gray-300 opacity-60'"
      >
        <div class="flex items-center space-x-4 mb-6">
          <div class="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center">
            <span class="material-symbols-outlined text-purple-600 text-3xl">groups</span>
          </div>
          <div>
            <h3 class="text-xl font-bold text-gray-900">Group Tuition</h3>
            <p class="text-sm text-gray-500">Content Library + weekly group sessions</p>
          </div>
        </div>
        <div class="space-y-3 mb-8">
          <div class="flex items-center space-x-3">
            <span class="material-symbols-outlined text-green-500">check</span>
            <span class="text-sm text-gray-700">Content Library access</span>
          </div>
          <div class="flex items-center space-x-3">
            <span class="material-symbols-outlined text-green-500">check</span>
            <span class="text-sm text-gray-700 font-semibold">Weekly group sessions / subject course</span>
          </div>
          <div class="flex items-center space-x-3">
            <span class="material-symbols-outlined text-green-500">check</span>
            <span class="text-sm text-gray-700">Live Q&A sessions</span>
          </div>
          <div class="flex items-center space-x-3">
            <span class="material-symbols-outlined text-green-500">check</span>
            <span class="text-sm text-gray-700">Community forum access</span>
          </div>
        </div>

        <!-- Spacer to push content to bottom -->
        <div class="flex-grow"></div>

        <!-- Not Offered Message -->
        <div v-if="!hasSilverMethods" class="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
          <div class="flex items-center gap-2 text-yellow-800">
            <span class="material-symbols-outlined text-lg">info</span>
            <p class="text-sm font-medium">You do not offer this service</p>
          </div>
          <p class="text-xs text-yellow-700 mt-1">Go back to Services Offered and select group tutoring methods to enable this tier</p>
        </div>

        <!-- Group Tutoring Subjects Button -->
        <div v-else-if="userSubjects.length > 0" class="mb-6">
          <div class="flex items-center justify-between mb-3">
            <span class="text-sm font-semibold text-gray-700">Group Tutoring Subjects</span>
            <span class="text-xs text-purple-600 font-medium">{{ selectedGroupSubjectsCount }} selected</span>
          </div>

          <button
            type="button"
            @click="openSubjectModal"
            class="w-full p-4 border-2 border-purple-200 rounded-xl hover:border-purple-400 hover:bg-purple-50 transition-all flex items-center justify-between group"
          >
            <span class="text-sm text-gray-700 font-medium">Select subjects to offer</span>
            <span class="material-symbols-outlined text-purple-600 group-hover:translate-x-1 transition-transform">arrow_forward</span>
          </button>
        </div>

        <!-- Pricing Control -->
        <div v-if="hasSilverMethods" class="pricing-control mt-6">
          <div class="flex justify-between items-center mb-4">
            <span class="text-sm font-semibold text-gray-700">Weekly Price</span>
            <span class="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
              Recommended: £{{ pricingLimits.silver?.average || 50 }}
            </span>
          </div>
          <div class="relative mb-6">
            <input
              v-model.number="silverPrice"
              type="range"
              :min="pricingLimits.silver?.min || 20"
              :max="pricingLimits.silver?.max || 80"
              class="w-full h-3 rounded-lg appearance-none cursor-pointer slider"
              :class="silverSliderColor"
              :style="{
                background: `linear-gradient(to right,
                  ${silverGradientColor} 0%,
                  ${silverGradientColor} ${((silverPrice - (pricingLimits.silver?.min || 20)) / ((pricingLimits.silver?.max || 80) - (pricingLimits.silver?.min || 20))) * 100}%,
                  #e5e7eb ${((silverPrice - (pricingLimits.silver?.min || 20)) / ((pricingLimits.silver?.max || 80) - (pricingLimits.silver?.min || 20))) * 100}%,
                  #e5e7eb 100%)`
              }"
            >
            <div class="flex justify-between text-xs text-gray-500 mt-2">
              <span>£{{ pricingLimits.silver?.min || 20 }}</span>
              <span>£{{ pricingLimits.silver?.max || 80 }}</span>
            </div>
          </div>
          <div class="text-center bg-purple-50 rounded-xl py-6">
            <span class="text-4xl font-bold text-gray-900">£{{ silverPrice }}</span>
            <span class="text-gray-500 text-lg font-bold"> weekly subscription</span>
          </div>
          <!-- Error message for silver pricing -->
          <div class="mt-3 min-h-[3rem]">
            <div v-if="errors['pricing.silver']" class="p-3 bg-red-50 border border-red-200 rounded-lg">
              <div class="flex items-start gap-2">
                <span class="material-symbols-outlined text-red-600 text-sm">error</span>
                <p class="text-sm text-red-700">{{ errors['pricing.silver'] }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Gold Plan -->
    <section id="gold-plan-pricing">
      <div
        class="border-2 rounded-2xl p-8 h-full transition-all relative overflow-hidden flex flex-col"
        :class="hasGoldMethods
          ? 'bg-white border-yellow-200 hover:border-yellow-400 hover:shadow-lg'
          : 'bg-gray-50 border-gray-300 opacity-60'"
      >
        <div v-if="hasGoldMethods" class="absolute top-0 right-0 bg-yellow-400 text-yellow-900 text-xs font-bold px-4 py-1 rounded-bl-lg">
          POPULAR
        </div>
        <div class="flex items-center space-x-4 mb-6">
          <div class="w-16 h-16 bg-yellow-100 rounded-2xl flex items-center justify-center">
            <span class="material-symbols-outlined text-yellow-600 text-3xl">stars</span>
          </div>
          <div>
            <h3 class="text-xl font-bold text-gray-900">1:1 Tuition</h3>
            <p class="text-sm text-gray-500">Content Library + Weekly 1:1 sessions</p>
          </div>
        </div>
        <div class="space-y-3 mb-8">
          <div class="flex items-center space-x-3">
            <span class="material-symbols-outlined text-green-500">check</span>
            <span class="text-sm text-gray-700">Content Library access</span>
          </div>
          <div class="flex items-center space-x-3">
            <span class="material-symbols-outlined text-green-500">check</span>
            <span class="text-sm text-gray-700 font-semibold">Weekly 1:1 sessions</span>
          </div>
          <div class="flex items-center space-x-3">
            <span class="material-symbols-outlined text-green-500">check</span>
            <span class="text-sm text-gray-700">Direct messaging with tutor</span>
          </div>
          <div class="flex items-center space-x-3">
            <span class="material-symbols-outlined text-green-500">check</span>
            <span class="text-sm text-gray-700">Personalized study plans</span>
          </div>
          <div class="flex items-center space-x-3">
            <span class="material-symbols-outlined text-green-500">check</span>
            <span class="text-sm text-gray-700">Priority support</span>
          </div>
        </div>

        <!-- Spacer to push pricing to bottom -->
        <div class="flex-grow"></div>

        <!-- Not Offered Message -->
        <div v-if="!hasGoldMethods" class="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
          <div class="flex items-center gap-2 text-yellow-800">
            <span class="material-symbols-outlined text-lg">info</span>
            <p class="text-sm font-medium">You do not offer this service</p>
          </div>
          <p class="text-xs text-yellow-700 mt-1">Go back to Services Offered and select 1:1 tutoring methods to enable this tier</p>
        </div>

        <!-- Pricing Control -->
        <div v-else class="pricing-control mt-6">
          <div class="flex justify-between items-center mb-4">
            <span class="text-sm font-semibold text-gray-700">Weekly Price</span>
            <span class="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
              Recommended: £{{ pricingLimits.gold?.average || 150 }}
            </span>
          </div>
          <div class="relative mb-6">
            <input
              v-model.number="goldPrice"
              type="range"
              :min="pricingLimits.gold?.min || 100"
              :max="pricingLimits.gold?.max || 200"
              class="w-full h-3 rounded-lg appearance-none cursor-pointer slider"
              :class="goldSliderColor"
              :style="{
                background: `linear-gradient(to right,
                  ${goldGradientColor} 0%,
                  ${goldGradientColor} ${((goldPrice - (pricingLimits.gold?.min || 100)) / ((pricingLimits.gold?.max || 200) - (pricingLimits.gold?.min || 100))) * 100}%,
                  #e5e7eb ${((goldPrice - (pricingLimits.gold?.min || 100)) / ((pricingLimits.gold?.max || 200) - (pricingLimits.gold?.min || 100))) * 100}%,
                  #e5e7eb 100%)`
              }"
            >
            <div class="flex justify-between text-xs text-gray-500 mt-2">
              <span>£{{ pricingLimits.gold?.min || 100 }}</span>
              <span>£{{ pricingLimits.gold?.max || 200 }}</span>
            </div>
          </div>
          <div class="text-center bg-yellow-50 rounded-xl py-6">
            <span class="text-4xl font-bold text-gray-900">£{{ goldPrice }}</span>
            <span class="text-gray-500 text-lg font-bold"> weekly subscription</span>
          </div>
          <!-- Error message for gold pricing -->
          <div class="mt-3 min-h-[3rem]">
            <div v-if="errors['pricing.gold']" class="p-3 bg-red-50 border border-red-200 rounded-lg">
              <div class="flex items-start gap-2">
                <span class="material-symbols-outlined text-red-600 text-sm">error</span>
                <p class="text-sm text-red-700">{{ errors['pricing.gold'] }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>

  <!-- Subject Selection Modal -->
  <SubjectSelectionModal
    :show="showSubjectModal"
    :subjects="userSubjects"
    :levels="userLevels"
    :selected-subjects="selectedGroupSubjects"
    @close="showSubjectModal = false"
    @update:selected="handleSubjectsUpdate"
  />
</template>

<style scoped>
/* Custom slider styling */
input[type="range"].slider {
  -webkit-appearance: none;
  appearance: none;
  height: 12px;
  border-radius: 6px;
  outline: none;
}

input[type="range"].slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: white;
  border: 3px solid currentColor;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  transition: transform 0.2s ease;
}

input[type="range"].slider::-webkit-slider-thumb:hover {
  transform: scale(1.1);
}

input[type="range"].slider::-moz-range-thumb {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: white;
  border: 3px solid currentColor;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  transition: transform 0.2s ease;
}

input[type="range"].slider::-moz-range-thumb:hover {
  transform: scale(1.1);
}

input[type="range"].slider::-moz-range-track {
  height: 12px;
  border-radius: 6px;
}

.accent-blue-500 {
  color: #3b82f6;
}

.accent-amber-500 {
  color: #f59e0b;
}
</style>
